import { useEffect, useRef, useState } from "react";
import { animate, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMAGES } from "../../data/content";

const MaskedWords = ({ text, delay = 0, className = "" }) => (
  <span className={className}>
    {text.split(" ").map((w, i) => (
      <span key={i} className="inline-block overflow-hidden pb-1 -mb-1 align-bottom">
        <motion.span
          className="inline-block"
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.85, delay: delay + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
        >
          {w}{" "}
        </motion.span>
      </span>
    ))}
  </span>
);

const ScriptText = ({ text, delay = 0, className = "" }) => (
  <span className={className}>
    {text.split("").map((ch, i) => (
      <motion.span
        key={i}
        className="inline-block"
        initial={{ opacity: 0, y: 18, rotate: -4 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 0.5, delay: delay + i * 0.035, ease: [0.16, 1, 0.3, 1] }}
      >
        {ch === " " ? " " : ch}
      </motion.span>
    ))}
  </span>
);

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: d, ease: [0.16, 1, 0.3, 1] } }),
};
const HERO_KICKER = ["Engineering", "Design", "Execution", "Consultancy"];

const HeroKicker = () => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.65, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
    className="relative mb-8 grid max-w-full grid-cols-2 gap-x-4 gap-y-2 overflow-hidden rounded-full border border-white/15 bg-white/[0.06] px-4 py-2.5 backdrop-blur-md sm:inline-flex sm:flex-wrap sm:items-center sm:gap-x-5 sm:px-5 sm:py-3"
    data-testid="hero-overline"
  >
    <motion.span
      aria-hidden="true"
      className="absolute left-4 right-4 top-1/2 h-px origin-left bg-[#F47721]/50 sm:left-5 sm:right-5"
      initial={{ scaleX: 0, opacity: 0 }}
      animate={{ scaleX: 1, opacity: [0, 1, 1, 0.18] }}
      transition={{ scaleX: { duration: 0.7, delay: 0.18, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 1.2, delay: 0.18, times: [0, 0.35, 0.8, 1] } }}
    />
    {HERO_KICKER.map((word, i) => (
      <span key={word} className="relative z-10 flex items-center gap-x-3 sm:gap-x-5">
        <motion.span
          data-testid={`hero-kicker-word-${i + 1}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.45 + i * 0.11, ease: [0.16, 1, 0.3, 1] }}
          className="font-manrope text-[11px] sm:text-sm md:text-base font-semibold uppercase tracking-[0.12em] sm:tracking-[0.16em] text-white/90"
        >
          {word}
        </motion.span>
        {i < HERO_KICKER.length - 1 && (
          <motion.span
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, delay: 0.55 + i * 0.11, ease: "easeOut" }}
            className={`h-1.5 w-1.5 rounded-full bg-[#F47721] ${i === 1 ? "hidden sm:block" : ""}`}
          />
        )}
      </span>
    ))}
  </motion.div>
);


const popVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.9 },
  show: (d) => ({ opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 120, damping: 14, delay: d } }),
};

const StatNumber = ({ value }) => {
  const num = parseInt(value, 10);
  const suffix = value.replace(/[0-9]/g, "");
  const [n, setN] = useState(0);
  useEffect(() => {
    const controls = animate(0, num, { duration: 1.8, delay: 1.5, ease: "easeOut", onUpdate: (v) => setN(Math.round(v)) });
    return () => controls.stop();
  }, [num]);
  return <>{String(n).padStart(2, "0")}{suffix}</>;
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  const scrollTo = (href) => (e) => {
    e.preventDefault();
    window.__lenis?.scrollTo(href, { offset: -76, duration: 1.4 });
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <motion.img
          src={IMAGES.hero}
          alt="Modern architectural structure"
          className="w-full h-full object-cover"
          animate={{ scale: [1, 1.12, 1], x: [0, -25, 0], y: [0, -18, 0] }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-navy/75" />
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-navy to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 w-full pb-16 pt-28 sm:pb-20 sm:pt-32 lg:pb-24 lg:pt-40">
        <HeroKicker />

        <h1 className="font-display font-black tracking-tighter leading-[0.98] text-[38px] sm:text-5xl md:text-6xl lg:text-7xl text-white">
          <span className="block">
            <MaskedWords text="FROM CONCEPT" delay={0.25} />
          </span>
          <span className="block">
            <MaskedWords text="TO" delay={0.5} /> <MaskedWords text="COMMISSIONING." delay={0.65} className="text-brand" />
          </span>
          <span className="block pt-2" style={{ textShadow: "0 3px 18px rgba(0,0,0,0.5)" }}>
            <ScriptText
              text="Excellence Delivered."
              delay={1.15}
              className="font-script font-normal tracking-normal normal-case text-brand text-[42px] sm:text-6xl md:text-7xl lg:text-8xl"
            />
          </span>
        </h1>
        <motion.span
          aria-hidden="true"
          className="block h-[3px] bg-brand mt-5 origin-left"
          style={{ width: "min(340px, 55%)" }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
        />

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
          <motion.p
            custom={0.9}
            variants={fadeVariants}
            initial="hidden"
            animate="show"
            className="max-w-md text-base md:text-lg leading-relaxed text-slate-200"
            data-testid="hero-tagline"
          >
            Delivering integrated engineering solutions with innovation, quality, safety and commitment. Building better. Delivering excellence.
          </motion.p>

          <motion.div custom={1.05} variants={popVariants} initial="hidden" animate="show" className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:gap-4">
            <motion.a
              href="#contact"
              onClick={scrollTo("#contact")}
              data-testid="hero-cta-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-brand text-white font-semibold py-4 px-8 hover:bg-brand-hover transition-colors duration-300 flex w-full sm:w-auto items-center justify-center gap-2"
            >
              Start a Project <ArrowRight size={18} />
            </motion.a>
            <motion.a
              href="#services"
              onClick={scrollTo("#services")}
              data-testid="hero-cta-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="border border-white/30 text-white font-semibold py-4 px-8 hover:bg-white hover:text-navy transition-colors duration-300 flex w-full sm:w-auto justify-center"
            >
              Explore Services
            </motion.a>
          </motion.div>
        </div>

        <motion.div
          custom={1.2}
          variants={fadeVariants}
          initial="hidden"
          animate="show"
          className="mt-12 pt-6 border-t border-white/15 grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8"
          data-testid="hero-stats"
        >
          {[
            ["15+", "Years of Expertise"],
            ["07", "Service Verticals"],
            ["06", "Industries Served"],
            ["100%", "Commitment to Safety"],
          ].map(([v, l], i) => (
            <motion.div
              key={l}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.3 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-white"><StatNumber value={v} /></p>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] text-slate-300 mt-2">{l}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 right-8 hidden lg:flex items-center gap-3 text-slate-300"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
